/**
 * DocumentReferenceVisual - Renders an embedded document with display options
 *
 * Wraps DocumentVisual with ReferenceWrapper to apply display options.
 * Handles circular reference gracefully by showing placeholder when
 * resolvedDocument is null (detected by BlockResolutionService).
 */

import { ReferenceWrapper, type ReferenceDisplayOptions } from "./ReferenceWrapper"
import { DocumentVisual } from "../document/DocumentVisual"
import type { BlockVisualProps } from "../../Types"
import type { DocumentReferenceBlock, ResolvedDocument } from "@/types/blocks"

export function DocumentReferenceVisual({ block }: BlockVisualProps) {
	// Type guard - return null for non-document_reference blocks
	if (block.type !== "document_reference") return null

	const refBlock = block as DocumentReferenceBlock
	const resolvedDocument = refBlock.properties.__resolved_document as ResolvedDocument | null
	const displayOptions = (refBlock.properties.__display_options ?? {}) as ReferenceDisplayOptions

	// No resolved document - show placeholder
	// This handles both missing documents and circular reference detection
	if (!resolvedDocument) {
		return (
			<div className="document-reference-empty">
				<p style={{ color: "var(--muted-foreground)" }}>Document not found</p>
			</div>
		)
	}

	// Build document block with resolved children for DocumentVisual
	const documentBlockWithResolved = {
		...resolvedDocument.document,
		properties: {
			...resolvedDocument.document.properties,
			__resolved_children: resolvedDocument.children,
		},
	}

	return (
		<ReferenceWrapper displayOptions={displayOptions}>
			<DocumentVisual block={documentBlockWithResolved} />
		</ReferenceWrapper>
	)
}
